import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';

/**
 * Super-admin editor for the TCN tenant-default `recruitment_bonus_config`.
 *
 * The super admin sets:
 *   - the MODE: 'fixed' (same paliers for all TCN agencies) or 'per_agency'
 *     (each agency edits its own, pre-filled from this default).
 *   - the default completion paliers (flat USD `amount` per completion `pct`).
 *
 * Per-agency editing lives in the TCN admin area (/tcn/admin/recruitment-bonus),
 * available to tenant_admin + agency_owner when mode = 'per_agency'.
 *
 * Recruitment bonus is a TCN concept, so this tab targets the `tcn` tenant.
 */
const RecruitmentTab = () => {
  const { t } = useTranslation();
  const [tenantId, setTenantId] = useState(null);
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSavedAt(null);
    const { data: tenant, error: tErr } = await supabase
      .from('tenants').select('id').eq('slug', 'tcn').maybeSingle();
    if (tErr || !tenant) {
      setError(tErr?.message || 'TCN tenant not found');
      setLoading(false);
      return;
    }
    setTenantId(tenant.id);
    const { data, error: dbErr } = await supabase
      .from('recruitment_bonus_config')
      .select('*')
      .eq('tenant_id', tenant.id)
      .is('agency_id', null)
      .maybeSingle();
    if (dbErr) setError(dbErr.message);
    setRow(data || { tenant_id: tenant.id, agency_id: null, server: 'MENA', mode: 'fixed', completion_tiers: [], _new: true });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const tiers = row?.completion_tiers || [];
  const updateTier = (idx, field, value) => {
    const next = [...tiers];
    next[idx] = { ...next[idx], [field]: value === '' ? null : Number(value) };
    setRow({ ...row, completion_tiers: next });
  };
  const removeTier = (idx) => setRow({ ...row, completion_tiers: tiers.filter((_, i) => i !== idx) });
  const addTier = () => setRow({ ...row, completion_tiers: [...tiers, { pct: 0, amount: 0 }] });
  const sortedTiers = useMemo(
    () => [...tiers].map((tr, i) => ({ ...tr, _origIdx: i })).sort((a, b) => (a.pct ?? 0) - (b.pct ?? 0)),
    [tiers]
  );

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setError(null);
    setSavedAt(null);
    const cleanTiers = (row.completion_tiers || [])
      .filter((tr) => tr.pct != null && tr.amount != null)
      .sort((a, b) => Number(a.pct) - Number(b.pct))
      .map((tr) => ({ pct: Number(tr.pct), amount: Number(tr.amount) }));
    const payload = { tenant_id: tenantId, agency_id: null, server: 'MENA', mode: row.mode, completion_tiers: cleanTiers };

    // Manual upsert (partial unique index on tenant_id WHERE agency_id IS NULL
    // can't be a supabase onConflict target).
    let dbErr;
    if (row.id) {
      ({ error: dbErr } = await supabase.from('recruitment_bonus_config').update(payload).eq('id', row.id));
    } else {
      ({ error: dbErr } = await supabase.from('recruitment_bonus_config').insert(payload));
    }
    if (dbErr) setError(dbErr.message);
    else setSavedAt(Date.now());
    setSaving(false);
    await load();
  };

  const inputBase =
    'w-full px-3 py-2 rounded-md bg-card border border-border text-foreground text-sm outline-none focus:border-accent focus:ring-1 focus:ring-accent/30 disabled:opacity-60';

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.businessRules.recruitment.intro',
          'TCN recruitment bonus. Pick the mode, then set the default paliers (completion % → flat USD amount).'
        )}
      </p>

      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">{error}</div>
      )}

      {loading ? (
        <div className="space-y-2">
          <div className="h-12 rounded-xl skeleton" />
          <div className="h-32 rounded-xl skeleton" />
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border p-5 space-y-5">
          {/* Mode selector */}
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              {t('superadmin.businessRules.recruitment.mode', 'Mode')}
            </label>
            <div className="flex gap-2 mt-2">
              {[
                { id: 'fixed', label: t('superadmin.businessRules.recruitment.modeFixed', 'Fixed for all TCN') },
                { id: 'per_agency', label: t('superadmin.businessRules.recruitment.modePerAgency', 'Free per agency') },
              ].map((m) => {
                const active = row.mode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setRow({ ...row, mode: m.id })}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                      active ? 'bg-accent text-white border-accent' : 'bg-card text-muted-foreground border-border hover:border-accent/50'
                    }`}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {row.mode === 'per_agency'
                ? t('superadmin.businessRules.recruitment.hintPerAgency', 'Each agency edits its own paliers (pre-filled from the default below) in the TCN admin area.')
                : t('superadmin.businessRules.recruitment.hintFixed', 'These paliers apply to every TCN agency (agencies cannot change them).')}
            </p>
          </div>

          {/* Default paliers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                {row.mode === 'per_agency'
                  ? t('superadmin.businessRules.recruitment.defaultTiers', 'Default paliers (per-agency starting point)')
                  : t('superadmin.businessRules.recruitment.tiers', 'Completion paliers')}
              </label>
              <button type="button" onClick={addTier}
                className="px-3 py-1 text-xs font-semibold rounded-md bg-primary text-white hover:bg-primary/90 transition-colors">
                + {t('superadmin.businessRules.recruitment.addTier', 'Add palier')}
              </button>
            </div>
            {sortedTiers.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                {t('superadmin.businessRules.recruitment.noTiers', 'No paliers yet. Add at least one (completion % → $ amount).')}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="hidden md:grid grid-cols-[1fr_1fr_auto] gap-3 px-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  <span>{t('superadmin.businessRules.recruitment.pct', 'Completion %')}</span>
                  <span>{t('superadmin.businessRules.recruitment.amount', 'Bonus ($)')}</span>
                  <span />
                </div>
                {sortedTiers.map((tier) => (
                  <div key={tier._origIdx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-center">
                    <input type="number" step="any" value={tier.pct ?? ''} placeholder="50"
                      onChange={(e) => updateTier(tier._origIdx, 'pct', e.target.value)} className={`${inputBase} tabular-nums`} />
                    <input type="number" step="any" value={tier.amount ?? ''} placeholder="100"
                      onChange={(e) => updateTier(tier._origIdx, 'amount', e.target.value)} className={`${inputBase} tabular-nums`} />
                    <button type="button" onClick={() => removeTier(tier._origIdx)}
                      className="px-3 py-2 text-sm rounded-md text-error hover:bg-error/10 transition-colors" aria-label="Remove palier">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
            <div className="text-xs text-muted-foreground">
              {savedAt && t('superadmin.savedAt', 'Saved {{time}}', { time: new Date(savedAt).toLocaleTimeString() })}
            </div>
            <button type="button" onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-lg bg-accent text-white text-sm font-semibold hover:bg-accent/90 disabled:opacity-50 transition-colors">
              {saving ? t('superadmin.businessRules.saving', 'Saving…') : t('superadmin.businessRules.save', 'Save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruitmentTab;
